import { useRef, useState } from "react";
import {
  FileUp,
  Loader2,
  AlertCircle,
  CheckCircle2,
  ArrowRight,
  AlertTriangle,
  Info,
} from "lucide-react";
import api from "../lib/api";
import Modal from "./Modal";
import { toast } from "./ui/sonner";

/**
 * Wave 26.6 — IQ Retail CRD list importer (suppliers).
 *
 * Two-step flow: upload CSV → dry-run preview → confirm commit.
 * Backend: POST /api/suppliers/import-iq-retail (mode=dry_run|commit).
 *
 * Why a dedicated modal (instead of using the existing BulkImportModal)?
 * The IQ Retail CSV has a fixed, known column structure
 * (ACCOUNT, NAME, TELEPHONE1, INITIALS, TITLE, CONTACT, ADDRESS1,
 *  DELIVER1..4, D_POSTCODE, CELLPHONE, EMAIL). The backend does
 * deterministic mapping (no LLM cost), phone normalisation to +27,
 * email split on '/', address concatenation, and idempotent upsert
 * by external_ref. Bulk import via Claude vision is unnecessary here
 * and would be 200x slower for a 229-row file.
 */
export default function IqRetailImportModal({ open, onClose, onImported }) {
  const [stage, setStage] = useState("pick"); // pick | preview | committing | done
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const reset = () => {
    setStage("pick");
    setFile(null);
    setBusy(false);
    setErr("");
    setPreview(null);
    setResult(null);
  };

  const close = () => {
    reset();
    onClose?.();
  };

  const onPick = (f) => {
    if (!f) return;
    if (!/\.csv$/i.test(f.name)) {
      setErr("Please pick a .csv file exported from IQ Retail.");
      return;
    }
    setErr("");
    setFile(f);
  };

  const runDryRun = async () => {
    if (!file) return;
    setBusy(true);
    setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", "dry_run");
      const { data } = await api.post("/suppliers/import-iq-retail", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      setStage("preview");
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Could not preview the file.");
    } finally {
      setBusy(false);
    }
  };

  const runCommit = async () => {
    if (!file) return;
    setStage("committing");
    setBusy(true);
    setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", "commit");
      const { data } = await api.post("/suppliers/import-iq-retail", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      setStage("done");
      const total = (data.created || 0) + (data.updated || 0);
      toast.success(`Imported ${total} suppliers (${data.created} new, ${data.updated} updated).`);
      onImported?.(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Import failed.");
      setStage("preview"); // back to preview so user can retry
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title="Import suppliers from IQ Retail (CRD list)"
      maxWidth="max-w-xl"
      testId="iq-retail-import-modal"
    >
      {stage === "pick" && (
        <PickStage
          file={file}
          err={err}
          busy={busy}
          fileInputRef={fileInputRef}
          onPick={onPick}
          onPreview={runDryRun}
        />
      )}

      {stage === "preview" && preview && (
        <PreviewStage
          preview={preview}
          err={err}
          onCancel={reset}
          onCommit={runCommit}
        />
      )}

      {stage === "committing" && (
        <div className="py-8 text-center" data-testid="iq-retail-importing">
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#1FB6C1] mb-2" />
          <div className="text-sm text-[#dde3ec]">Importing… do not close.</div>
          <div className="text-[10px] text-[#647087] mt-1">
            Writing {(preview?.will_create_count || 0) + (preview?.will_update_count || 0)} suppliers.
          </div>
        </div>
      )}

      {stage === "done" && result && (
        <DoneStage result={result} onClose={close} />
      )}
    </Modal>
  );
}

function PickStage({ file, err, busy, fileInputRef, onPick, onPreview }) {
  return (
    <div className="space-y-3" data-testid="iq-retail-pick-stage">
      <div className="text-xs text-[#a0aabf] leading-relaxed">
        Export your <strong className="text-white">CRD (creditor) list</strong> from IQ Retail
        as a CSV with the standard columns
        (ACCOUNT, NAME, TELEPHONE1, CONTACT, ADDRESS1, DELIVER1-4, D_POSTCODE, CELLPHONE, EMAIL).
        We'll normalise phones to <code className="text-[#1FB6C1]">+27</code>,
        split addresses into delivery / postal, lower-case emails, and de-duplicate
        by ACCOUNT — so re-running this is safe.
      </div>

      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        className="w-full border-2 border-dashed border-white/15 rounded-lg p-6 hover:border-[#1FB6C1]/50 transition text-center"
        data-testid="iq-retail-pick-btn"
      >
        <FileUp className="w-7 h-7 text-[#647087] mx-auto mb-2" />
        <div className="text-sm">
          {file ? (
            <span className="text-[#1FB6C1] font-medium">{file.name}</span>
          ) : (
            <>Click to choose a <strong>.csv</strong> file</>
          )}
        </div>
        {file && (
          <div className="text-[10px] text-[#647087] mt-1">
            {(file.size / 1024).toFixed(1)} KB
          </div>
        )}
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        onChange={(e) => onPick(e.target.files?.[0])}
        data-testid="iq-retail-file-input"
      />

      {err && (
        <div className="text-xs text-rose-300 inline-flex items-center gap-1.5">
          <AlertCircle className="w-3.5 h-3.5" /> {err}
        </div>
      )}

      <button
        type="button"
        onClick={onPreview}
        disabled={!file || busy}
        className="hv-btn hv-btn-primary w-full justify-center"
        data-testid="iq-retail-preview-btn"
      >
        {busy ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" /> Reading file…
          </>
        ) : (
          <>
            <ArrowRight className="w-4 h-4" /> Preview changes
          </>
        )}
      </button>
    </div>
  );
}

function PreviewStage({ preview, err, onCancel, onCommit }) {
  const total = preview.total_rows;
  const c = preview.will_create_count;
  const u = preview.will_update_count;
  const s = preview.will_skip_count;
  const w = preview.warnings_count;
  const dups = preview.in_file_duplicate_account_codes || [];

  return (
    <div className="space-y-3" data-testid="iq-retail-preview-stage">
      <div className="text-xs text-[#a0aabf]">
        File: <code className="text-[#1FB6C1]">{preview.filename}</code> ·{" "}
        {total} rows
      </div>

      <div className="grid grid-cols-3 gap-2">
        <Stat label="New" value={c} accent="emerald" testId="iq-retail-stat-create" />
        <Stat label="Update" value={u} accent="cyan" testId="iq-retail-stat-update" />
        <Stat label="Skip" value={s} accent="rose" testId="iq-retail-stat-skip" />
      </div>

      {dups.length > 0 && (
        <div className="text-[11px] text-amber-300 border border-amber-500/30 bg-amber-500/5 rounded p-2 inline-flex items-start gap-1.5">
          <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
          <span>
            <strong>{dups.length}</strong> ACCOUNT code(s) appear more than once
            in the file ({dups.slice(0, 3).join(", ")}
            {dups.length > 3 ? "…" : ""}). The last occurrence will win on commit.
          </span>
        </div>
      )}

      {w > 0 && (
        <div className="text-[11px] text-[#a0aabf] border border-white/10 rounded p-2">
          <div className="inline-flex items-center gap-1.5 text-amber-300 font-bold mb-1">
            <Info className="w-3 h-3" /> {w} warning{w === 1 ? "" : "s"}
          </div>
          <ul className="space-y-0.5 max-h-24 overflow-y-auto">
            {(preview.warnings || []).slice(0, 6).map((wn, i) => (
              <li key={i} className="text-[10px]">
                <span className="font-mono text-[#647087]">row {wn.row}</span> ·{" "}
                <span className="text-[#1FB6C1]">{wn.external_ref}</span> —{" "}
                {(wn.messages || []).join("; ")}
              </li>
            ))}
            {w > 6 && (
              <li className="text-[10px] text-[#647087] italic">
                + {w - 6} more (will be applied silently on commit)
              </li>
            )}
          </ul>
        </div>
      )}

      <details className="text-[11px]" data-testid="iq-retail-preview-rows">
        <summary className="cursor-pointer text-[#647087] hover:text-white">
          Show first 10 new + first 10 updated rows
        </summary>
        <div className="mt-2 grid sm:grid-cols-2 gap-2">
          <RowList title="To create" items={preview.will_create || []} accent="emerald" />
          <RowList title="To update" items={preview.will_update || []} accent="cyan" showDiff />
        </div>
      </details>

      {err && (
        <div className="text-xs text-rose-300 inline-flex items-center gap-1.5">
          <AlertCircle className="w-3.5 h-3.5" /> {err}
        </div>
      )}

      <div className="flex gap-2 pt-2 border-t border-white/5">
        <button
          type="button"
          onClick={onCancel}
          className="hv-btn hv-btn-ghost flex-1 justify-center"
          data-testid="iq-retail-cancel"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onCommit}
          disabled={c + u === 0}
          className="hv-btn hv-btn-primary flex-1 justify-center"
          data-testid="iq-retail-commit"
        >
          <CheckCircle2 className="w-4 h-4" /> Import {c + u} suppliers
        </button>
      </div>
    </div>
  );
}

function DoneStage({ result, onClose }) {
  const total = (result.created || 0) + (result.updated || 0);
  return (
    <div className="space-y-3 text-center py-3" data-testid="iq-retail-done-stage">
      <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto" />
      <div className="text-base font-bold">Imported {total} suppliers</div>
      <div className="text-xs text-[#a0aabf]">
        <span className="text-emerald-300">{result.created} created</span> ·{" "}
        <span className="text-[#1FB6C1]">{result.updated} updated</span> ·{" "}
        <span className="text-[#647087]">{result.skipped} skipped</span>
      </div>
      <div className="text-[10px] text-[#647087] font-mono">
        Batch: {result.import_batch_id?.slice(0, 8)}…
      </div>
      {(result.errors || []).length > 0 && (
        <div className="text-[10px] text-rose-300 text-left border border-rose-500/30 bg-rose-500/5 rounded p-2 max-h-24 overflow-y-auto">
          <div className="font-bold mb-1">Errors:</div>
          {result.errors.slice(0, 5).map((e, i) => (
            <div key={i}>{e}</div>
          ))}
        </div>
      )}
      <button
        type="button"
        onClick={onClose}
        className="hv-btn hv-btn-primary w-full justify-center"
        data-testid="iq-retail-done-close"
      >
        Done
      </button>
    </div>
  );
}

function Stat({ label, value, accent, testId }) {
  const palette = {
    emerald: "border-emerald-500/30 text-emerald-300 bg-emerald-500/5",
    cyan: "border-[#1FB6C1]/30 text-[#1FB6C1] bg-[#1FB6C1]/5",
    rose: "border-rose-500/30 text-rose-300 bg-rose-500/5",
  };
  return (
    <div
      className={`rounded border p-2 text-center ${palette[accent]}`}
      data-testid={testId}
    >
      <div className="text-2xl font-bold font-mono">{value}</div>
      <div className="text-[10px] uppercase tracking-wider opacity-80">{label}</div>
    </div>
  );
}

function RowList({ title, items, accent, showDiff }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1">
        {title} ({items.length})
      </div>
      <ul className="space-y-0.5 max-h-40 overflow-y-auto border border-white/5 rounded p-1">
        {items.slice(0, 10).map((r, i) => (
          <li key={i} className="text-[10px]">
            <span className={`font-mono text-${accent}-300`}>{r.external_ref}</span>{" "}
            <span className="text-[#dde3ec]">{r.name}</span>
            {showDiff && r.diff_keys?.length > 0 && (
              <span className="text-[#647087] block pl-2">
                Δ {r.diff_keys.join(", ")}
              </span>
            )}
          </li>
        ))}
        {items.length > 10 && (
          <li className="text-[10px] text-[#647087] italic">
            + {items.length - 10} more
          </li>
        )}
      </ul>
    </div>
  );
}
